import PropTypes from 'prop-types';
import PhoneInput from 'react-phone-input-2';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { censorTypes, useCensor } from '../../utils/hooks/useCensor/useCensor';
import LoadingSkeleton from '../LoadingSkeleton';
import './PhoneNumberInput.scss';

// Workaround to fix an export that isn't valid with Vite
// https://github.com/vitejs/vite/issues/2139#issuecomment-1024852072
const PhoneInputComponent = PhoneInput.default || PhoneInput;

function PhoneNumberInput({
  loading = false,
  label = '',
  placeholder = '',
  value = '',
  onChange = () => {},
  isValid = null,
  error = '',
  description = '',
}) {
  const { censor, isCensorActive } = useCensor();

  const validate = (inputNumber, country) => {
    // Use passed isValid prop if provided, otherwise use internal validation
    if (isValid !== null) {
      return isValid;
    }

    // empty is valid
    return (
      inputNumber === '' || //we see iso2 and countryCode depending on which callback is being hit 🙃
      isValidPhoneNumber(inputNumber, country?.iso2?.toUpperCase() ?? country?.countryCode?.toUpperCase())
    );
  };

  const handleChange = (phoneValue, country) => {
    // if the user double clicks and clears the phone number, "+" will be the only value remaining.
    // Not sure why. This is a workaround to prevent "+" from being sent to the onChange callback. - Kurt
    if (phoneValue?.length === 1 && phoneValue?.[0] === '+') {
      onChange('', true);
      return;
    }

    onChange(phoneValue, validate(phoneValue, country));
  };

  if (loading) {
    return (
      <div className="PhoneNumberInput">
        <LoadingSkeleton className="PhoneNumberInput-label--loader" />
        <LoadingSkeleton className="PhoneNumberInput-input--loader" />
        {description && <LoadingSkeleton className="PhoneNumberInput-description--loader" />}
        {error && <LoadingSkeleton className="PhoneNumberInput-error--loader" />}
      </div>
    );
  }

  return (
    <div className="PhoneNumberInput">
      <label className="PhoneNumberInput-label">{label}</label>
      {isCensorActive ? (
        <div>{censor(value, censorTypes.pii)}</div>
      ) : (
        <PhoneInputComponent
          value={value}
          priority={{ ca: 0, us: 1 }}
          placeholder={placeholder}
          isValid={validate}
          onChange={handleChange}
        />
      )}
      {error && <div className="PhoneNumberInput-error">{error}</div>}
      {description && <div className="PhoneNumberInput-description">{description}</div>}
    </div>
  );
}

PhoneNumberInput.propTypes = {
  loading: PropTypes.bool,
  isCensored: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  isValid: PropTypes.bool,
  error: PropTypes.string,
  description: PropTypes.string,
};

export default PhoneNumberInput;
